import { ArrowDown, ArrowUp, Pencil, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { serviceTypesApi } from '@/api/service-types'
import { PermissionGuard } from '@/components/auth/PermissionGuard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { showError } from '@/lib/error'
import type { ServiceType } from '@/types/service-record'

interface ServiceTypeTableProps {
  types: ServiceType[]
  onEdit: (t: ServiceType) => void
  onDelete: (t: ServiceType) => void
  onChanged: () => void
  /** 关键词过滤时禁用排序：仅展示子集，重排会损坏全局 sort */
  sortingDisabled?: boolean
}

async function move(types: ServiceType[], index: number, direction: -1 | 1, onChanged: () => void) {
  const target = index + direction
  if (target < 0 || target >= types.length) return
  const ids = types.map(t => t.id)
  ;[ids[index], ids[target]] = [ids[target], ids[index]]
  try {
    await serviceTypesApi.reorder(ids)
    onChanged()
  } catch (error) {
    showError(error, '调整排序失败')
  }
}

async function toggleStatus(t: ServiceType, enabled: boolean, onChanged: () => void) {
  try {
    await serviceTypesApi.updateStatus(t.id, enabled)
    toast.success(enabled ? `「${t.label}」已启用` : `「${t.label}」已停用`)
    onChanged()
  } catch (error) {
    showError(error, '切换状态失败')
  }
}

export function ServiceTypeTable({ types, onEdit, onDelete, onChanged, sortingDisabled = false }: ServiceTypeTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>类型名称</TableHead>
          <TableHead>状态</TableHead>
          <TableHead>备注</TableHead>
          <TableHead>要求图片</TableHead>
          <TableHead className="w-64">排序</TableHead>
          <TableHead className="text-right">操作</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {types.map((t, index) => (
          <TableRow key={t.id}>
            <TableCell className="font-medium">{t.label}</TableCell>
            <TableCell>
              <PermissionGuard
                permission="service_type:update"
                fallback={<Badge variant={t.enabled ? 'default' : 'secondary'}>{t.enabled ? '启用' : '停用'}</Badge>}
              >
                <Switch checked={t.enabled} onCheckedChange={v => toggleStatus(t, v, onChanged)} />
              </PermissionGuard>
            </TableCell>
            <TableCell>{t.remark || '-'}</TableCell>
            <TableCell>
              {t.is_avatar ? <Badge>需图片</Badge> : <span className="text-muted-foreground">-</span>}
            </TableCell>
            <TableCell>
              <PermissionGuard permission="service_type:update">
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" disabled={sortingDisabled || index === 0} onClick={() => move(types, index, -1, onChanged)}>
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" disabled={sortingDisabled || index === types.length - 1} onClick={() => move(types, index, 1, onChanged)}>
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                </div>
              </PermissionGuard>
            </TableCell>
            <TableCell className="text-right">
              <div className="flex items-center justify-end gap-1">
                <PermissionGuard permission="service_type:update">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(t)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                </PermissionGuard>
                <PermissionGuard permission="service_type:delete">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => onDelete(t)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </PermissionGuard>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
